import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import withNavigate from "../withNavigate";
import moment from "moment-timezone";
import { getDailyNotificationActivityLogs } from "../../modules/Notification";
import { modal } from "../../modules/Dialogs";
import { sessionExpired } from "../../config/GeneralConfig";
import { toScreen } from "../../modules/Routing";
import ChannelsTab from "./ChannelsTab";
import ActivityLogTab from "./ActivityLogTab";
import NotificationLogsTab from "./NotificationLogsTab";
import DailySummaryTab from "./DailySummaryTab";
import MonthlySummaryTab from "./MonthlySummaryTab";

const TABS = [
  { id: "channels",         label: "Channels" },
  { id: "activityLog",      label: "Activity log" },
  { id: "notificationLogs", label: "Notification logs" },
  { id: "dailySummary",     label: "Daily summary" },
  { id: "monthlySummary",   label: "Monthly summary" },
];

const Notifications = ({ userDetails, navigate }) => {
  const [activeTab, setActiveTab] = useState("channels");
  const [dailyStats, setDailyStats] = useState({
    sent: 0,
    failed: 0,
    abandoned: 0,
    activeChannels: 0,
  });

  const fetchDailyStats = useCallback(() => {
    getDailyNotificationActivityLogs(
      { date: moment().format("YYYY-MM-DD") },
      (resStatus, _responseString, resJson) => {
        if (resStatus === 401) {
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
        } else if (resStatus === 200 || resStatus === "200") {
          const fetched = resJson?.payload || resJson?.data || [];
          if (Array.isArray(fetched) && fetched.length > 0) {
            setDailyStats({
              sent:           fetched.reduce((s, i) => s + (i.sent || i.totalSent || 0), 0),
              failed:         fetched.reduce((s, i) => s + (i.failed || i.totalFailed || 0), 0),
              abandoned:      fetched.reduce((s, i) => s + (i.abandoned || i.totalAbandoned || 0), 0),
              activeChannels: fetched.reduce((s, i) => s + (i.activeChannels || i.active_channels || 0), 0),
            });
          }
        }
      },
      userDetails.jwttoken,
    );
  }, [userDetails.jwttoken, navigate]);

  useEffect(() => {
    if (userDetails?.jwttoken) fetchDailyStats();
  }, [userDetails?.jwttoken, fetchDailyStats]);

  const renderActiveTab = () => {
    const props = { userDetails, navigate };
    switch (activeTab) {
      case "channels":         return <ChannelsTab {...props} />;
      case "activityLog":      return <ActivityLogTab {...props} />;
      case "notificationLogs": return <NotificationLogsTab {...props} />;
      case "dailySummary":     return <DailySummaryTab {...props} />;
      case "monthlySummary":   return <MonthlySummaryTab {...props} />;
      default:                 return null;
    }
  };

  return (
    <div className="l-base-layout__content">
      <h1>Admin / Notifications</h1>
      <div className="c-hr"></div>

      {/* Stats cards */}
      <div className="l-row">
        <div className="l-col-25">
          <div className="c-info-notification-block c-info-notification-block--sent">
            <div className="c-info-notification-block__header">
              <span className="c-info-notification-block__label">Sent today</span>
            </div>
            <div className="c-info-notification-block__value">
              {dailyStats.sent.toLocaleString()}
            </div>
          </div>
        </div>
        <div className="l-col-25">
          <div className="c-info-notification-block c-info-notification-block--failed">
            <div className="c-info-notification-block__header">
              <span className="c-info-notification-block__label">Failed today</span>
            </div>
            <div className="c-info-notification-block__value">
              {dailyStats.failed.toLocaleString()}
            </div>
          </div>
        </div>
        <div className="l-col-25">
          <div className="c-info-notification-block c-info-notification-block--abandoned">
            <div className="c-info-notification-block__header">
              <span className="c-info-notification-block__label">Abandoned channels</span>
            </div>
            <div className="c-info-notification-block__value">
              {dailyStats.abandoned.toLocaleString()}
            </div>
          </div>
        </div>
        <div className="l-col-25">
          <div className="c-info-notification-block c-info-notification-block--active">
            <div className="c-info-notification-block__header">
              <span className="c-info-notification-block__label">Active channels</span>
            </div>
            <div className="c-info-notification-block__value">
              {dailyStats.activeChannels.toLocaleString()}
            </div>
          </div>
        </div>
        <div style={{ clear: "both" }}></div>
      </div>

      {/* Tab navigation */}
      <div className="my-16">
        <div className="custom-tabs">
          {TABS.map((tab) => (
            <div
              key={tab.id}
              className={`tab-item ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </div>
          ))}
        </div>
      </div>

      {/* Active tab content */}
      {renderActiveTab()}
    </div>
  );
};

Notifications.propTypes = {
  userDetails: PropTypes.object,
  navigate:    PropTypes.func.isRequired,
};

export default withNavigate(Notifications);
